import { Button } from "@wags/ui/button.tsx";
import type { ReactNode } from "react";
import { SkeletonBox } from "skullet/react";
import { Header } from "@/components/Header";
import { MenuSection } from "@/components/shared/MenuSection";
import { SettingsBackLink } from "@/components/shared/SettingsBackLink";
import type { LoginSecurityView } from "./login-security";

export function LoginSecuritySurface({
    view,
    onSignIn,
    onConnectGoogle,
    onSignOut,
    showHeader = false,
    pending = false,
    error = null,
    onRetryLinkedProviders,
}: {
    view: LoginSecurityView;
    onSignIn: () => void;
    onConnectGoogle: () => void;
    onSignOut: () => void;
    showHeader?: boolean;
    pending?: boolean;
    error?: string | null;
    onRetryLinkedProviders: () => void;
}) {
    return (
        <div className="w-full bg-maltese-white">
            {showHeader && <Header className="hidden md:block" />}

            <main className="mx-auto flex w-full max-w-[1138px] flex-col px-5 pt-8 pb-28 md:px-6 md:pt-[132px] md:pb-24">
                <div className="mb-8 flex flex-col gap-4 md:mb-11">
                    <SettingsBackLink to="/settings" label="Settings" />
                    <h1 className="text-page-title text-doberman-black">
                        Login &amp; security
                    </h1>
                </div>

                <fieldset
                    disabled={pending}
                    aria-busy={pending}
                    className="flex min-w-0 flex-col gap-6 md:gap-8"
                >
                    {pending ? <output>Working…</output> : null}
                    {error ? <p role="alert">{error}</p> : null}
                    <section className="flex flex-col">
                        <p className="border-b border-doberman-black/8 pb-3 text-label text-doberman-black/55">
                            Account
                        </p>
                        {view.kind === "loading" ? (
                            <output className="py-4 text-body-small text-doberman-black/55">
                                Loading account…
                            </output>
                        ) : (
                            <AccountRow
                                {...accountRowContent(view, {
                                    onSignIn,
                                    onConnectGoogle,
                                    onRetryLinkedProviders,
                                })}
                            />
                        )}
                    </section>

                    {view.kind === "member" ? (
                        <MenuSection
                            items={[
                                {
                                    label: "Sign out",
                                    muted: true,
                                    onClick: onSignOut,
                                },
                            ]}
                        />
                    ) : null}
                </fieldset>
            </main>
        </div>
    );
}

type AccountRowContent = {
    title: string;
    detail: ReactNode;
    action: ReactNode;
    busy: boolean;
};

function accountRowContent(
    view: Exclude<LoginSecurityView, { kind: "loading" }>,
    actions: {
        onSignIn: () => void;
        onConnectGoogle: () => void;
        onRetryLinkedProviders: () => void;
    },
): AccountRowContent {
    if (view.kind === "guest") {
        return {
            title: "You're browsing as a guest",
            detail: (
                <RowDetail>
                    Sign in to keep saved dogs, matches, and Kits conversations
                    across devices.
                </RowDetail>
            ),
            action: (
                <Button
                    variant="dark"
                    className="h-11 w-fit shrink-0 px-5"
                    onClick={actions.onSignIn}
                >
                    Sign in
                </Button>
            ),
            busy: false,
        };
    }
    const title = view.identity;
    switch (view.google) {
        case "pending":
            return {
                title,
                detail: (
                    <SkeletonBox
                        className="mt-2"
                        width={176}
                        height={12}
                        radius="var(--radius-label)"
                    />
                ),
                action: null,
                busy: true,
            };
        case "failed":
            return {
                title,
                detail: (
                    <RowDetail role="alert">
                        Google connection status couldn&apos;t load.
                    </RowDetail>
                ),
                action: (
                    <Button
                        variant="outline"
                        size="touch"
                        className="w-fit shrink-0"
                        onClick={actions.onRetryLinkedProviders}
                    >
                        Try again
                    </Button>
                ),
                busy: false,
            };
        case "linked":
            return {
                title,
                detail: (
                    <RowDetail>Google is connected to this account.</RowDetail>
                ),
                action: (
                    <span className="w-fit shrink-0 rounded-label bg-havanese px-3 py-1 text-body-small text-doberman-black/55">
                        Connected
                    </span>
                ),
                busy: false,
            };
        case "unlinked":
            return {
                title,
                detail: (
                    <RowDetail>
                        Connect Google to sign in on any device.
                    </RowDetail>
                ),
                action: (
                    <Button
                        variant="dark"
                        className="h-11 w-fit shrink-0 px-5"
                        onClick={actions.onConnectGoogle}
                    >
                        Connect Google
                    </Button>
                ),
                busy: false,
            };
    }
}

function RowDetail({
    children,
    role,
}: {
    children: ReactNode;
    role?: "alert";
}) {
    return (
        <p role={role} className="mt-1 text-body-small text-doberman-black/55">
            {children}
        </p>
    );
}

function AccountRow({ title, detail, action, busy }: AccountRowContent) {
    return (
        <div className="flex min-h-[74px] flex-col gap-4 py-4 sm:flex-row sm:items-center sm:justify-between md:min-h-[83px]">
            <div className="min-w-0" aria-busy={busy}>
                <p className="break-words text-body font-semibold text-doberman-black">
                    {title}
                </p>
                {detail}
            </div>
            {action}
        </div>
    );
}
